import type { ClaimClientUpdateConfiguration } from '@shared/constants/business/claim';
import { addBusinessDays } from '@shared/utils';
import type { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';

export function getIncreasingAdjustment(excessAmount: Decimal) {
    return excessAmount.div(11);
}

/**
 * The due date of a new client-update task: `intervalDays` after the org-local day of `from`, stored
 * as UTC midnight of the due day like every existing row. The day is taken in the org's zone, not
 * the server's UTC clock, which is still on the previous day before 10:00 or 11:00 in Sydney.
 */
export function getClaimClientUpdateDueDate({
    from,
    zone,
    intervalDays,
    dayIntervalType,
}: {
    from: Date;
    /** The org's IANA report timezone. */
    zone: string;
} & Pick<ClaimClientUpdateConfiguration, 'intervalDays' | 'dayIntervalType'>) {
    const local = DateTime.fromJSDate(from, { zone });
    if (!local.isValid) {
        // addBusinessDays never advances from an invalid date, so fail here instead of spinning.
        throw new RangeError(
            `Cannot build a client update due date: ${local.invalidExplanation ?? local.invalidReason}`,
        );
    }
    const startDay = DateTime.utc(local.year, local.month, local.day);

    return dayIntervalType === 'BUSINESS'
        ? addBusinessDays(startDay.toJSDate(), intervalDays, { zone: 'utc' })
        : startDay.plus({ days: intervalDays }).toJSDate();
}

/**
 * The calendar day a client-update due date names, at its start in the org's zone. System-created
 * due dates are date-only values stored as UTC midnight, so their UTC date is the due day in any
 * zone, including one behind UTC. Any other instant, such as a due date edited in the browser (local
 * midnight), is read in the org's zone.
 */
export function getClaimClientUpdateDueDay(dueDate: Date, zone: string): DateTime {
    const utc = DateTime.fromJSDate(dueDate, { zone: 'utc' });

    return utc.toMillis() === utc.startOf('day').toMillis()
        ? DateTime.fromObject({ year: utc.year, month: utc.month, day: utc.day }, { zone })
        : DateTime.fromJSDate(dueDate, { zone }).startOf('day');
}

/**
 * Weekdays from `from` (inclusive) to `to` (exclusive), negative when `to` is earlier: the same count
 * as `differenceInBusinessDays(to, from)`, but whole weeks are counted at once, so a task due weeks
 * away costs no more than one due tomorrow. Both arguments are the start of a day in one zone.
 */
function countBusinessDays(from: DateTime, to: DateTime): number {
    if (to < from) {
        return -countBusinessDays(to, from);
    }

    const days = Math.round(to.diff(from, 'days').days);
    const fullWeeks = Math.floor(days / 7);
    let count = fullWeeks * 5;
    let day = from.plus({ weeks: fullWeeks });
    for (let remaining = days % 7; remaining > 0; remaining--) {
        if (day.weekday <= 5) {
            count++;
        }
        day = day.plus({ days: 1 });
    }

    return count;
}

/**
 * Days from the day of `asOf` to a client-update task's due day: negative when overdue, 0 on the due
 * day. Both are read as calendar days in the org's zone, so the 07:00 reminder job, the claim header
 * and the dashboard agree on the same Sydney day although the server clock runs in UTC.
 */
export function calculateClaimClientUpdateDifferenceInDays({
    dueDate,
    asOf,
    zone,
    dayIntervalType,
}: {
    dueDate: Date;
    asOf: Date;
    /** The org's IANA report timezone. */
    zone: string;
} & Pick<ClaimClientUpdateConfiguration, 'dayIntervalType'>) {
    const dueDay = getClaimClientUpdateDueDay(dueDate, zone);
    const day = DateTime.fromJSDate(asOf, { zone }).startOf('day');

    if (dayIntervalType === 'BUSINESS') {
        return countBusinessDays(day, dueDay);
    }

    // Days across a daylight-saving change are 23 or 25 hours long.
    return Math.round(dueDay.diff(day, 'days').days);
}
